import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { GraduationCap, Clock, Calendar, CheckCircle, LogOut, Trophy } from 'lucide-react';

export default function StudentDashboard() {
  const [exams, setExams] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    fetchExams();
    fetchResults();
  }, []);

  const fetchExams = async () => {
    try {
      const response = await axios.get(`${API}/student/exams`);
      setExams(response.data);
    } catch (error) {
      toast.error('Failed to fetch exams');
    } finally {
      setLoading(false);
    }
  };

  const fetchResults = async () => {
    try {
      const response = await axios.get(`${API}/student/results`);
      setResults(response.data);
    } catch (error) {
      toast.error('Failed to fetch results');
    }
  };

  const handleStartExam = (examId) => {
    navigate(`/student/exam/${examId}`);
  };

  const handleViewResult = (attemptId) => {
    navigate(`/student/result/${attemptId}`);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    toast.success('Logged out successfully');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-sky-100 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <GraduationCap className="h-7 w-7 text-sky-600" />
            <div>
              <h1 className="text-xl font-bold text-gray-900" style={{fontFamily: 'Space Grotesk, sans-serif'}}>ESAP</h1>
              <p className="text-sm text-gray-500">Student Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium text-gray-900">{user?.name}</p>
              <p className="text-xs text-gray-500">{user?.email}</p>
            </div>
            <Button variant="outline" onClick={handleLogout} data-testid="logout-btn" className="border-sky-200">
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="exams" className="space-y-6">
          <TabsList className="bg-white border border-sky-100" data-testid="student-tabs">
            <TabsTrigger value="exams" data-testid="exams-tab">Available Exams</TabsTrigger>
            <TabsTrigger value="results" data-testid="results-tab">My Results</TabsTrigger>
          </TabsList>

          <TabsContent value="exams" className="space-y-4">
            {loading ? (
              <div className="text-center py-12">
                <div className="spinner mx-auto mb-4"></div>
                <p className="text-gray-500">Loading exams...</p>
              </div>
            ) : exams.length === 0 ? (
              <Card className="border-sky-100">
                <CardContent className="py-12 text-center">
                  <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No exams available at the moment</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {exams.map((exam) => (
                  <Card key={exam.id} className="border-sky-100 hover:shadow-lg transition-shadow" data-testid={`exam-card-${exam.id}`}>
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <CardTitle className="text-xl">{exam.title}</CardTitle>
                        {exam.attempt_submitted ? (
                          <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Completed</Badge>
                        ) : exam.attempted ? (
                          <Badge className="bg-yellow-100 text-yellow-700 hover:bg-yellow-100">In Progress</Badge>
                        ) : (
                          <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Available</Badge>
                        )}
                      </div>
                      <CardDescription className="space-y-2 mt-3">
                        <div className="flex items-center gap-2 text-sm">
                          <Clock className="h-4 w-4" />
                          <span>Duration: {exam.duration_minutes} minutes</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Trophy className="h-4 w-4" />
                          <span>Total Marks: {exam.total_marks}</span>
                        </div>
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {exam.attempt_submitted ? (
                        <Button 
                          variant="outline" 
                          className="w-full" 
                          disabled
                          data-testid={`exam-completed-${exam.id}`}
                        >
                          <CheckCircle className="mr-2 h-4 w-4" />
                          Completed
                        </Button>
                      ) : (
                        <Button 
                          className="w-full bg-sky-600 hover:bg-sky-700"
                          onClick={() => handleStartExam(exam.id)}
                          data-testid={`start-exam-${exam.id}`}
                        >
                          {exam.attempted ? 'Resume Exam' : 'Start Exam'}
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="results" className="space-y-4">
            {results.length === 0 ? (
              <Card className="border-sky-100">
                <CardContent className="py-12 text-center">
                  <Trophy className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No results yet. Take an exam to see your results here.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {results.map((result) => {
                  const percentage = (result.score / result.total_marks * 100).toFixed(1);
                  return (
                    <Card key={result.id} className="border-sky-100 hover:shadow-md transition-shadow" data-testid={`result-card-${result.id}`}>
                      <CardContent className="py-6">
                        <div className="flex justify-between items-start">
                          <div className="space-y-2 flex-1">
                            <h3 className="text-lg font-semibold text-gray-900">{result.exam_title}</h3>
                            <div className="flex gap-6 text-sm text-gray-600">
                              <span>Score: <span className="font-semibold text-sky-600">{result.score}/{result.total_marks}</span></span>
                              <span>Percentage: <span className="font-semibold text-sky-600">{percentage}%</span></span>
                              <span>Time: {Math.floor(result.time_spent_seconds / 60)} min</span>
                            </div>
                          </div>
                          <Button 
                            onClick={() => handleViewResult(result.id)}
                            data-testid={`view-result-${result.id}`}
                            className="bg-sky-600 hover:bg-sky-700"
                          >
                            View Details
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}